import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular';
import {
  CommunicationMessagesService,
  DevicesService,
  NetworkdevicesService,
} from '@ubie/core';
import { NetworkDeviceActivationMessage } from '@ubie/mqttcommunication';
import { helpCircleOutline } from 'ionicons/icons';
import { ActivationDevice } from '../../../interfaces/activation-device.interface';
import { ErrorModalComponent } from '../../shared/error-modal/error-modal.component';

@Component({
  selector: 'acl-activation-modal',
  standalone: false,
  templateUrl: './activation-modal.component.html',
  styleUrl: './activation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivationModalComponent {
  private readonly modalController = inject(ModalController);
  private readonly communicationMessagesService: CommunicationMessagesService =
    inject(CommunicationMessagesService);
  private readonly devicesService = inject(DevicesService);
  private readonly networkDeviceService = inject(NetworkdevicesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly title = input('');
  readonly message = input('');
  readonly activationDevice = input.required<ActivationDevice>();

  readonly showChoice = signal(true);
  readonly activationDone = signal(false);
  readonly activationProgress = signal(0);
  readonly activationMessage = signal('');
  readonly progressValue = computed(() => this.activationProgress() / 100);
  readonly helpCircleOutline = helpCircleOutline;

  private activationListenerInitialized = false;

  /**
   * Yes clicked --> activate controller
   */
  onYesClicked(): void {
    if (!this.activationDevice()) return;
    this.ensureActivationListener();
    this.showChoice.set(false);

    this.networkDeviceService
      .getNetworkDeviceApiKey(this.activationDevice().networkDeviceId)
      .subscribe({
        next: (apiKey: string) => {
          this.activationDevice().verificationKey = apiKey;
          this.activateDevice();
        },
        error: () => {
          this.openErrorModal();
        },
      });
  }

  async dismiss(): Promise<void> {
    this.modalController.dismiss();
  }

  activateDevice(): void {
    if (!this.activationDevice()) return;
    this.devicesService
      .activateController(
        this.activationDevice().ubieSerial,
        this.activationDevice().networkDeviceSerial,
        this.activationDevice().networkDeviceId,
        this.activationDevice().verificationKey,
      )
      .subscribe({
        error: () => {
          this.openErrorModal();
        },
      });
  }

  private ensureActivationListener(): void {
    if (this.activationListenerInitialized) return;
    this.activationListenerInitialized = true;
    this.setUpActivationMessageListener();
  }

  private setUpActivationMessageListener(): void {
    this.communicationMessagesService
      .getCtdActivationMessages$(
        this.activationDevice().ubieSerial,
        this.activationDevice().networkDeviceSerial,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message: NetworkDeviceActivationMessage) => {
          this.activationMessage.set(message.message);
          this.activationProgress.set((100 / message.maxStep) * message.step);
          if (message.step === message.maxStep) {
            this.activationDone.set(true);
            this.modalController.dismiss('reload');
          }
        },
      });
  }

  async openErrorModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: ErrorModalComponent,
      componentProps: {
        title: 'device_activate',
        message: 'device_apiKeyFailed',
      },
    });

    return await modal.present();
  }
}
